import { HttpErrorResponse } from '@angular/common/http';
import { injectMutation, type QueryClient } from '@tanstack/angular-query-experimental';
import type { NotificationsService } from '../services/notifications.service';

export type MutationCallbacks<TData, TError = Error, TVariables = void> = {
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: TError, variables: TVariables) => void;
};

export type Text = string | (() => string);

const resolve = (text: Text): string => (typeof text === 'function' ? text() : text);

export interface WriteMutationOptions<TData, TVariables> {
  key: string;
  mutationFn: (variables: TVariables) => Promise<TData>;
  successMessage: string | ((data: TData, variables: TVariables) => string);
  callbacks?: MutationCallbacks<TData, Error, TVariables>;
}

export function writeMutation<TData, TVariables>(
  queryClient: QueryClient,
  notifications: NotificationsService,
  allKeys: readonly unknown[],
  saveFailedFallback: Text,
  options: WriteMutationOptions<TData, TVariables>,
) {
  return injectMutation(() => ({
    mutationKey: [...allKeys, options.key],
    mutationFn: options.mutationFn,
    onSuccess: async (data: TData, variables: TVariables) => {
      await queryClient.invalidateQueries({ queryKey: allKeys });
      notifications.success(
        typeof options.successMessage === 'function'
          ? options.successMessage(data, variables)
          : options.successMessage,
      );
      options.callbacks?.onSuccess?.(data, variables);
    },
    onError: (error: Error, variables: TVariables) => {
      notifications.error(errorMessage(error, resolve(saveFailedFallback)));
      options.callbacks?.onError?.(error, variables);
    },
  }));
}

export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const body = error.error;
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object' && typeof body.message === 'string' && body.message) {
      return body.message;
    }
    return error.message || fallback;
  }
  return error instanceof Error && error.message ? error.message : fallback;
}
